import Vue from 'vue'
import appConfig from '@/config.js'
import store from '@/store'
import { showErrorAlert } from '@/common/errorAlert'

const ApiService = {
  query(resource, params) {
    return Vue.http.get(appConfig.api.baseUrl + resource, params)
  },

  get(resource, id = '', params) {
    return Vue.http.get(`${appConfig.api.baseUrl}${resource}/${id}`, params)
  },

  post(resource, params) {
    return Vue.http.post(`${appConfig.api.baseUrl}${resource}`, params)
  },

  update(resource, id, params) {
    return Vue.http.patch(`${appConfig.api.baseUrl}${resource}/${id}`, params)
  },

  put(resource, params) {
    return Vue.http.put(`${appConfig.api.baseUrl}${resource}`, params)
  },

  patch(resource, params) {
    return Vue.http.patch(`${appConfig.api.baseUrl}${resource}`, params)
  },

  delete(resource, params) {
    return Vue.http.delete(appConfig.api.baseUrl + resource, params)
  }
}

export default ApiService

export const ShippingsService = {
  query (type, params) {
    return ApiService
      .query(
        'users/' + store.state.auth.uid + '/shippings' + (type === 'feed' ? '/feed' : ''),
        { params }
      )
  },
  get (id, params) {
    return ApiService.get('users/' + store.state.auth.uid + '/shippings', id, {params})
  },
  create (params) {
    return ApiService.post('users/' + store.state.auth.uid + '/shippings', {params})
  },
  update (id, params) {
    return ApiService.update('users/' + store.state.auth.uid + '/shippings', id, {shipping: params})
  },
  destroy (id) {
    return ApiService.delete(`users/${store.state.auth.uid}/shippings/${id}`)
  },
  attachFile(id, payload) {
    const formData = new FormData()
    formData.append('file', payload.file, payload.file.name)
    return ApiService.post('files', formData)
      .then(response => {
        payload.full_path = response.body.full_path
        if (!payload.comment) {
          delete payload.comment
        }
        if (!payload.type) {
          delete payload.type
        }
        return ApiService.post(`shippings/${id}/photos`, payload)
      })
  },
  deleteAttachment(id, attachmentId) {
    return ApiService.delete(`shippings/${id}/photos/${attachmentId}`)
  }
}

export const PaymentMethodsService = {
  query (type, params) {
    return ApiService
      .query(
        `users/${store.state.auth.uid}/payment-methods`,
        { params }
      )
  },
  create (params) {
    return ApiService.post(`users/${store.state.auth.uid}/payment-methods`, params)
  },
  update (id, params) {
    delete params.id
    return ApiService.update(`users/${store.state.auth.uid}/payment-methods`, id, params)
  },
  destroy (id) {
    return ApiService.delete(`users/${store.state.auth.uid}/payment-methods/${id}`)
  }
}

export const UsersService = {
  get () {
    return ApiService.get('users', store.state.auth.uid)
  },
  update (params) {
    return ApiService.update('users', store.state.auth.uid, params)
  },
  destroy () {
    return ApiService.delete(`users`, store.state.auth.uid)
  },
  changePassword (params) {
    return ApiService.put(`users/${store.state.auth.uid}/change-password`, params)
  }
}

export const AddressesService = {
  query (type, params) {
    return ApiService
      .query(
        'users/' + store.state.auth.uid + '/addresses',
        { params }
      )
  },
  create (params) {
    return ApiService.post(`users/${store.state.auth.uid}/addresses`, params)
  },
  update (id, params) {
    return ApiService.update('users/' + store.state.auth.uid + '/addresses', id, params)
  },
  destroy (id) {
    return ApiService.delete(`users/${store.state.auth.uid}/addresses/${id}`)
  }
}

export const FaqsService = {
  query (type, params) {
    return Vue.http
      .get(
        appConfig.helpdocs.baseUrl + 'posts/?filter[taxonomy]=category&filter[term]=' + appConfig.helpdocs.category + '&filter[tag]=' + appConfig.helpdocs.tag + '&per_page=100&key=' + appConfig.helpdocs.api_key,
        { params }
      )
  },
  get (id, params) {
    return Vue.http.get(appConfig.helpdocs.baseUrl + 'posts/' + id, id, {params})
  }
}

export const CommentsService = {
  get (id) {
    if (typeof id !== 'string') {
      throw new Error('[MVA] CommentsService.get() shipping id required to fetch comments')
    }
    return ApiService.get('users/' + Vue.$store.getters.uid + '/shippings', `${id}/comments`)
  },

  post (id, payload) {
    return ApiService.post(
      `users/${Vue.$store.getters.uid}/shippings/${id}/comments`, { comment: { body: payload } })
  },

  destroy (id, commentId) {
    return ApiService
      .delete(`users/${Vue.$store.getters.uid}/shippings/${id}/comments/${commentId}`)
  }
}

export const FavoriteService = {
  add (id) {
    return ApiService.post(`shippings/${id}/favorite`)
  },
  remove (id) {
    return ApiService.delete(`shippings/${id}/favorite`)
  }
}

export const ApplicationsService = {

  findMeliApplication ({ companyId, platformId, meliUserId }) {
    if (!platformId) {
      return Promise.resolve(null)
    }
    // TODO: Remove after total migration of meli to v2 in 11 september.
    const filters = [
      ['company_id', 'eq', companyId],
      ['platform_id', 'eq', 39]
    ]
    // Sin meliUserId no se puede desambiguar el vendedor: va la primera app de la empresa.
    if (meliUserId) {
      filters.push(['webhook_url', 'ew', `&userMeli=${meliUserId}`])
    }

    const params = {
      limit: 1,
      filter_groups: [{ filters }]
    }

    return ApiService.query('applications', { params })
      .then(response => response.body.data[0] || null)
  }
}

export const PlatformInformationService = {
  get (shipping) {
    const shippingId = shipping.id

    // TODO: sacar los silentError cuando termine la migración a meli v2.
    return Vue.http
      .get(`${appConfig.api.baseUrl}shippings/${shippingId}/platform-information`, { silentError: true })
      .catch(() =>
        this.getFromMeli(shipping).catch(error => {
          showErrorAlert(this.errorMessage(error))
          return Promise.reject(Object.assign(error || {}, { shippingId }))
        })
      )
  },

  errorMessage (error) {
    const body = error && error.body
    return (body && body.message) || (error && error.message) ||
      'No se pudo obtener la información de la plataforma'
  },

  getMeliShipping (shipping) {
    return this.getFromMeli(shipping).catch(error => {
      showErrorAlert(this.errorMessage(error))
      return Promise.reject(error)
    })
  },

  getFromMeli (shipping) {
    const meliUserId = shipping.extra?.meliUserId

    return ApplicationsService
      .findMeliApplication({
        companyId: shipping.company_id,
        platformId: shipping.platform_id,
        meliUserId
      })
      .then(application => {
        if (!application) {
          return Promise.reject(new Error(
            `no hay aplicación de meli para la empresa ${shipping.company_id}` +
            (meliUserId ? `, vendedor ${meliUserId}` : '')
          ))
        }

        return Vue.http.get(`${appConfig.meliApi.baseUrl}v2/meli/shippings/${shipping.external_code}`, {
          silentError: true,
          params: { appId: application.id },
          headers: {
            'x-api-key': appConfig.meliApi.apiKey,
            Authorization: application.key
          }
        })
      })
  }
}
